import { useState, useEffect, Fragment } from 'react';

/**
 * SlicersRail — right-side collapsible filter rail.
 *
 * Per Claude Design "Data Portal — Alignment" bundle (slicers-rail.jsx):
 *   Expanded ~340px with sectioned filter controls + presets + apply/reset.
 *   Collapsed 56px strip with per-section glyphs.
 *   Disabled state for pages with no slicers (e.g., /setup) — body greyed and inert.
 *
 * This is the Phase 5 chrome build — visual rail with representative filter
 * sections (date range, programs, states, cohorts, snapshot). Real filter
 * state binding to dashboards is a separate plumbing pass.
 */

interface SlicersRailProps {
  disabled?: boolean;
  disabledReason?: string | undefined;
}

interface RailState {
  dateRange: 'today' | 'last7' | 'last30' | 'last90' | 'qtd' | 'ytd' | 'my26';
  markets: string[];
  lob: 'mapd' | 'dsnp' | 'csnp' | 'commercial';
  programs: string[];
  cohorts: string[];
  snapshot: 'latest' | '2026-05-03' | '2026-04-26' | '2026-04-01';
}

const DEFAULTS: RailState = {
  dateRange: 'ytd',
  markets: ['CA', 'NV', 'AZ'],
  lob: 'mapd',
  // Empty default — the page-level URL slug is authoritative for the active
  // campaign. Was `['FOBT']` which auto-filtered every page to FOBT
  // regardless of the chip strip's selection. (William 2026-05-25.)
  programs: [],
  cohorts: ['diabetic-compliant', 'spanish-pref'],
  snapshot: 'latest',
};

const DATE_PRESETS: Array<{ id: RailState['dateRange']; label: string }> = [
  { id: 'today', label: 'Today' },
  { id: 'last7', label: '7d' },
  { id: 'last30', label: '30d' },
  { id: 'last90', label: '90d' },
  { id: 'qtd', label: 'QTD' },
  { id: 'ytd', label: 'YTD' },
  { id: 'my26', label: 'MY 2026' },
];
const MARKETS = ['CA', 'NV', 'AZ', 'NC', 'FL', 'TX'];
const LOBS: Array<{ id: RailState['lob']; label: string }> = [
  { id: 'mapd', label: 'MAPD' },
  { id: 'dsnp', label: 'D-SNP' },
  { id: 'csnp', label: 'C-SNP' },
  { id: 'commercial', label: 'Commercial' },
];
const PROGRAMS = ['FOBT', 'A1c & KED', 'COL', 'BCS'];
const COHORTS = ['diabetic-compliant', 'diabetic-noncompliant', 'spanish-pref', 'rising-risk', 'lapsed-engagement'];
const SNAPSHOTS: Array<{ id: RailState['snapshot']; label: string }> = [
  { id: 'latest', label: 'Latest · 2026-05-10 04:00' },
  { id: '2026-05-03', label: '2026-05-03 04:00' },
  { id: '2026-04-26', label: '2026-04-26 04:00' },
  { id: '2026-04-01', label: '2026-04-01 04:00 (M-end)' },
];

const STRIP_ITEMS: Array<{ key: string; glyph: string; label: string }> = [
  { key: 'date', glyph: '◷', label: 'DATE' },
  { key: 'market', glyph: '◉', label: 'MKT' },
  { key: 'lob', glyph: '▤', label: 'LOB' },
  { key: 'prog', glyph: '✦', label: 'PRG' },
  { key: 'cohort', glyph: '⌘', label: 'COH' },
  { key: 'snap', glyph: '⎍', label: 'SNP' },
];

export function SlicersRail({ disabled = false, disabledReason }: SlicersRailProps) {
  const [collapsed, setCollapsed] = useState(() => {
    if (typeof window === 'undefined') return false;
    return localStorage.getItem('cc-rail-collapsed') === '1';
  });
  const [state, setState] = useState<RailState>(() => {
    if (typeof window === 'undefined') return DEFAULTS;
    try {
      const saved = JSON.parse(localStorage.getItem('cc-slicers') || '{}') as Partial<RailState>;
      return { ...DEFAULTS, ...saved };
    } catch {
      return DEFAULTS;
    }
  });

  useEffect(() => {
    localStorage.setItem('cc-rail-collapsed', collapsed ? '1' : '0');
  }, [collapsed]);

  useEffect(() => {
    localStorage.setItem('cc-slicers', JSON.stringify(state));
  }, [state]);

  const toggleIn = <K extends 'markets' | 'programs' | 'cohorts'>(key: K, val: string) => {
    setState((prev) => {
      const cur = prev[key];
      const next = cur.includes(val) ? cur.filter((x) => x !== val) : [...cur, val];
      return { ...prev, [key]: next };
    });
  };

  const dirty = countDirty(state);

  const has = {
    date: state.dateRange !== DEFAULTS.dateRange,
    market: JSON.stringify(state.markets) !== JSON.stringify(DEFAULTS.markets),
    lob: state.lob !== DEFAULTS.lob,
    prog: JSON.stringify(state.programs) !== JSON.stringify(DEFAULTS.programs),
    cohort: JSON.stringify(state.cohorts) !== JSON.stringify(DEFAULTS.cohorts),
    snap: state.snapshot !== DEFAULTS.snapshot,
  };

  // Pages where slicing does not apply (e.g. Setup) get a plain beige panel
  // that still slides in/out, but with no filter controls or slicer language.
  if (disabled) {
    return (
      <aside
        className={`slicers-rail ${collapsed ? 'collapsed' : ''} disabled`}
        data-disabled="1"
        aria-label="Panel"
      >
        <button
          type="button"
          className="rail-toggle"
          onClick={() => setCollapsed((c) => !c)}
          title={collapsed ? 'Expand panel' : 'Collapse panel'}
          aria-label={collapsed ? 'Expand panel' : 'Collapse panel'}
        >
          {collapsed ? '◀' : '▶'}
        </button>
        <div className="rail-strip" aria-hidden="true" />
        {/* Plain-beige panel: show only the top "n/a on this page" message —
            no "Slicers · Filters" eyebrow, no filter sections, no footer.
            Still slides in/out via the toggle above. */}
        <div className="rail-body" style={{ background: 'var(--paper-2)' }}>
          <div>
            <div
              className="mono small"
              style={{
                fontSize: 10.5,
                color: 'var(--ink-400)',
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
              }}
            >
              Slicers · n/a on this page
            </div>
            <div style={{ fontSize: 12.5, color: 'var(--ink-700)', marginTop: 4, lineHeight: 1.5 }}>
              {disabledReason ?? 'This page reflects what was configured during onboarding. There is nothing to filter here.'}
            </div>
          </div>
        </div>
      </aside>
    );
  }

  return (
    <aside
      className={`slicers-rail ${collapsed ? 'collapsed' : ''} ${disabled ? 'disabled' : ''}`}
      data-disabled={disabled ? '1' : '0'}
      aria-label="Slicers rail"
    >
      <button
        type="button"
        className="rail-toggle"
        onClick={() => setCollapsed((c) => !c)}
        title={collapsed ? 'Expand slicers rail' : 'Collapse slicers rail'}
        aria-label={collapsed ? 'Expand slicers rail' : 'Collapse slicers rail'}
      >
        {collapsed ? '◀' : '▶'}
      </button>

      {/* Collapsed mini-strip */}
      <div className="rail-strip">
        <div className="rail-strip-label">Slicers</div>
        {STRIP_ITEMS.map((it, i) => (
          <Fragment key={it.key}>
            {(i === 2 || i === 4) && <div className="rail-strip-sep" />}
            <div
              className={`rail-strip-item ${(has as Record<string, boolean>)[it.key] ? 'has-value' : ''}`}
              title={it.label}
              onClick={() => setCollapsed(false)}
            >
              <span className="glyph">{it.glyph}</span>
              <span className="mini">{it.label}</span>
            </div>
          </Fragment>
        ))}
      </div>

      {/* Expanded body — enabled pages only. The disabled/Setup case returns
          the plain-beige message panel above and never reaches here. */}
      <div className="rail-body">
        <div className="rail-eyebrow">Slicers · Filters</div>

        <>
        {/* Date range */}
        <section className="rail-section">
          <div className="rail-section-title">
            <span>Date <em>range</em></span>
            <span className="aux">{state.dateRange.toUpperCase()}</span>
          </div>
          <div className="rail-chips">
            {DATE_PRESETS.map((p) => (
              <button
                key={p.id}
                type="button"
                className={`rail-chip ${state.dateRange === p.id ? 'on teal' : ''}`}
                onClick={() => setState((s) => ({ ...s, dateRange: p.id }))}
              >
                {p.label}
              </button>
            ))}
          </div>
        </section>

        {/* Markets */}
        <section className="rail-section">
          <div className="rail-section-title">
            <span><em>Markets</em></span>
            <span className="aux">{state.markets.length} sel.</span>
          </div>
          <div className="rail-chips">
            {MARKETS.map((m) => (
              <button
                key={m}
                type="button"
                className={`rail-chip ${state.markets.includes(m) ? 'on' : ''}`}
                onClick={() => toggleIn('markets', m)}
              >
                {m}
              </button>
            ))}
          </div>
        </section>

        {/* Line of business */}
        <section className="rail-section">
          <div className="rail-section-title">
            <span>Line of <em>business</em></span>
            <span className="aux">{state.lob.toUpperCase()}</span>
          </div>
          <div className="rail-chips">
            {LOBS.map((lob) => (
              <button
                key={lob.id}
                type="button"
                className={`rail-chip ${state.lob === lob.id ? 'on' : ''}`}
                onClick={() => setState((s) => ({ ...s, lob: lob.id }))}
              >
                {lob.label}
              </button>
            ))}
          </div>
        </section>

        {/* Programs */}
        <section className="rail-section">
          <div className="rail-section-title">
            <span><em>Programs</em></span>
            <span className="aux">{state.programs.length} sel.</span>
          </div>
          <div className="rail-chips">
            {PROGRAMS.map((p) => (
              <button
                key={p}
                type="button"
                className={`rail-chip ${state.programs.includes(p) ? 'on teal' : ''}`}
                onClick={() => toggleIn('programs', p)}
              >
                {p}
              </button>
            ))}
          </div>
        </section>

        {/* Cohorts */}
        <section className="rail-section">
          <div className="rail-section-title">
            <span><em>Cohorts</em></span>
            <span className="aux">{state.cohorts.length} sel.</span>
          </div>
          <div className="rail-chips">
            {COHORTS.map((co) => (
              <button
                key={co}
                type="button"
                className={`rail-chip ${state.cohorts.includes(co) ? 'on' : ''}`}
                onClick={() => toggleIn('cohorts', co)}
              >
                {co}
              </button>
            ))}
          </div>
        </section>

        {/* Snapshot */}
        <section className="rail-section">
          <div className="rail-section-title">
            <span>Snapshot</span>
            <span className="aux">pinned</span>
          </div>
          <select
            value={state.snapshot}
            onChange={(e) =>
              setState((s) => ({ ...s, snapshot: e.target.value as RailState['snapshot'] }))
            }
            className="rail-input"
          >
            {SNAPSHOTS.map((snap) => (
              <option key={snap.id} value={snap.id}>
                {snap.label}
              </option>
            ))}
          </select>
        </section>
        </>
      </div>

      {!disabled && (
      <div className="rail-footer">
        <span className="applied-count">
          <b>{dirty}</b> applied
        </span>
        <button
          type="button"
          className="btn-link"
          onClick={() => setState(DEFAULTS)}
          disabled={dirty === 0}
        >
          Reset
        </button>
        <button type="button" className="btn-apply">
          Apply
        </button>
      </div>
      )}
    </aside>
  );
}

function countDirty(s: RailState): number {
  let n = 0;
  if (s.dateRange !== DEFAULTS.dateRange) n++;
  if (JSON.stringify(s.markets) !== JSON.stringify(DEFAULTS.markets)) n++;
  if (s.lob !== DEFAULTS.lob) n++;
  if (JSON.stringify(s.programs) !== JSON.stringify(DEFAULTS.programs)) n++;
  if (JSON.stringify(s.cohorts) !== JSON.stringify(DEFAULTS.cohorts)) n++;
  if (s.snapshot !== DEFAULTS.snapshot) n++;
  return n;
}
